import {
  type MeanTimeBetweenFailuresResponse,
  type MtmYtmRequestPayload,
  formatDateUtcToTimezoneDateOnly,
  PERFORMANCE_KPI_REQUEST_STALE_TIME
} from '@domain/kpis'
import {ApiClient} from '@hconnect/apiclient'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'

const getMeanTimeBetweenFailuresMtmYtmUrlSuffix = (showYTM?: boolean) =>
  showYTM ? 'mean-time-between-failures-ytm' : 'mean-time-between-failures-mtm'

export const getMeanTimeBetweenFailuresMtmYtmQueryKeys = (requestPayload: MtmYtmRequestPayload) => [
  getMeanTimeBetweenFailuresMtmYtmUrlSuffix(requestPayload.showYTM),
  formatDateUtcToTimezoneDateOnly(requestPayload.timestamp, requestPayload.timezone),
  requestPayload.locationId
]

export const getMeanTimeBetweenFailures = async (
  {timestamp, timezone, locationId, showYTM}: MtmYtmRequestPayload,
  signal?: AbortSignal
) => {
  const referenceDate = formatDateUtcToTimezoneDateOnly(timestamp, timezone)
  const url = `/kpi/dashboard/${getMeanTimeBetweenFailuresMtmYtmUrlSuffix(showYTM)}`
  const response = await ApiClient.axiosInstance.get<MeanTimeBetweenFailuresResponse>(url, {
    params: {locationId, date: referenceDate},
    signal
  })

  return response.data
}

export const useMeanTimeBetweenFailures = <T>(
  payload: MtmYtmRequestPayload,
  options?: Omit<
    UseQueryOptions<MeanTimeBetweenFailuresResponse, AxiosError, T>,
    'queryKey' | 'queryFn'
  >
) => {
  const QueryKeys = getMeanTimeBetweenFailuresMtmYtmQueryKeys(payload)

  return useQuery<MeanTimeBetweenFailuresResponse, AxiosError, T>({
    queryKey: QueryKeys,
    queryFn: async ({signal}) => {
      return getMeanTimeBetweenFailures(payload, signal)
    },
    staleTime: PERFORMANCE_KPI_REQUEST_STALE_TIME,
    ...options
  })
}
